import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Input from '@mui/material/Input'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import { Controller, useForm } from 'react-hook-form'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import InfoIcon from '@mui/icons-material/Info'
import DropDownComponent, {
  DropDownElement
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import RefreshIcon from '@mui/icons-material/Refresh'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import { Option, RegistrationJsonField } from '@professocial/professocialTypes/types/commonTypes'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@mui/styles'
import { Captcha, GetNewCaptchaRequest } from '@professocialApi/professocial-core-frontend-api'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch, useSelector } from 'react-redux'
import { getNewCaptchaInitiated } from '../../../store/user/user.actions'
import { getCaptcha } from '../../../store/user/user.selector'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'

export type IRegistrationForm = {
  mandator?: string
  username?: string
  email?: string
  agency?: string
  password?: string
  password2?: string
  firstname?: string
  lastname?: string
  privacy?: boolean
  termsOfUse?: boolean
  homepage?: string
  motivation?: string
  captcha?: string
}

type Props = {
  headline?: string
  loading: boolean
  fields?: RegistrationJsonField[]
  isOpenCallback: (e: boolean) => void
  callbackOnSubmit: (captcha: Captcha | undefined, data: IRegistrationForm) => void
}

const useStyles = makeStyles(() => ({
  tooltip: {
    position: 'absolute',
    right: -34,
    top: 25,
  },
  isInvalid: {
    color: '#d32f2f',
  },
}))

const RegisterForm: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const dispatch = useDispatch()
  const dispatchGetNewCaptcha = useCallback((params: GetNewCaptchaRequest) => dispatch(getNewCaptchaInitiated(params)), [dispatch])

  const captcha: Captcha | undefined = useSelector(getCaptcha)
  const [captchaPicture, setCaptchaPicture] = useState<string>()

  const createYupSchema = (schema: any, field: RegistrationJsonField) => {
    let validator: any
    const passwordRegExpPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$%\-@_]).{8,20}$/
    switch (true) {
    case (field.type === 'text' || field.type === 'email' || field.type === 'password'):
      validator = Yup.string()
      if (field.pattern !== undefined && field.pattern !== null) {
        validator = validator.matches(
          new RegExp(field.name === 'password' ? passwordRegExpPattern : field.pattern),
          field.info ? field.info : '')
      }
      if (field.type === 'email') {
        validator = validator.email()
          .matches(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/, field.info || t('login:emailValidationError'))
      }
      if (field.equals === 'password' && field.type === 'password') {
        validator = validator.when('password', {
          is: (password: string) => password.length > 0,
          then: validator.test('passwords-match', `${t('accountsCommon:passwordsError')}`,
            (value: string) => field.equals === 'password' ? getValues('password') === value : false),
        })
      }
      schema[field.name] = validator
      break
    case (field.inputType === 'checkbox'):
      validator = Yup.bool().oneOf([true])
      schema[field.name] = validator
      break
    default:
      schema.captcha = Yup.string().required('')
    }

    return schema
  }
  const yepSchema = props.fields?.reduce(createYupSchema, {})
  const validateSchema = Yup.object().shape(yepSchema)

  const formMethods = useForm({
    mode: 'onChange',
    resolver: yupResolver(validateSchema),
  })

  const {
    handleSubmit, getValues, control, register,
    formState: { isDirty, errors },
  } = formMethods

  const onCancelEdit = () => {
    props.isOpenCallback(false)
  }

  const getDropDownOptions = (options: Option[] | undefined): DropDownElement[] => {
    const temp: DropDownElement[] = []
    options !== undefined && options.map((entry) => {
      temp.push({ value: entry.label, key: entry.value })
    })
    return temp
  }

  const fetchCaptcha = () => {
    const params: GetNewCaptchaRequest = {
      timestamp: new Date().toString(),
    }
    dispatchGetNewCaptcha(params)
  }

  const isTextarea = (dataEntry: RegistrationJsonField) => dataEntry.inputType === 'textarea'

  useEffect(() => {
    fetchCaptcha()
  }, [])

  useEffect(() => {
    captcha && setCaptchaPicture(captcha.picture)
  }, [captcha])


  return (
    <Grid container sx={{ px: 5, py: 2 }}>
      <Typography variant='h2' width='100%' textAlign='center' sx={{ mb: 3 }}>{props.headline}</Typography>
      <Grid container item xs={12} spacing={2} style={{ display: 'flex', justifyContent: 'center' }}>
        {props.fields?.map((dataEntry, index) => {
          return (
            <Grid
              item xs={12} key={index} sx={{
                position: 'relative',
                height: (dataEntry.type === 'hidden' || dataEntry.inputType === 'hidden') ? 0 : 'inherit',
                '&.MuiGrid-item': {
                  pt: dataEntry.type === 'hidden' || dataEntry.inputType === 'hidden' ? 0 : 2,
                },
              }}
            >
              {(dataEntry.type === 'hidden' || dataEntry.inputType === 'hidden') &&
                <Controller
                  name={dataEntry.name as any}
                  control={control}
                  defaultValue={dataEntry.value}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id={dataEntry.name}
                      type='hidden'
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />}
              {(dataEntry.inputType === 'input' || isTextarea(dataEntry)) && dataEntry.type !== 'hidden' &&
                <>
                  <TextFieldComponent
                    {...register(dataEntry.name as any)}
                    id={dataEntry.name}
                    label={dataEntry.label || ''}
                    type={dataEntry.type}
                    size='small'
                    multiline={isTextarea(dataEntry)}
                    rows={isTextarea(dataEntry) ? 3 : 1}
                    inputProps={{ placeholder: dataEntry.value }}
                    required={dataEntry.required}
                    error={!!errors[dataEntry.name]}
                    helperText={errors[dataEntry.name]?.message as ReactNode}
                    InputProps={dataEntry.type === 'password' ? { autoComplete: 'new-password' } : undefined}
                    fullWidth
                  />
                  {dataEntry.info &&
                    <Box className={classes.tooltip}>
                      <TooltipComponent
                        title={dataEntry.info}
                        type='html'
                        child={<InfoIcon color='primary' />}
                        placement='right'
                        arrow
                      />
                    </Box>}
                </>}
              {dataEntry.inputType === 'select' &&
                <>
                  <Controller
                    name={dataEntry.name as any}
                    control={control}
                    defaultValue=''
                    render={({ field: { onChange } }: { field: { onChange: any } }) => (
                      <DropDownComponent
                        id={dataEntry.name}
                        options={getDropDownOptions(dataEntry.option)}
                        label={dataEntry.label || ''}
                        size='small'
                        onChange={onChange}
                      />
                    )}
                  />
                  {dataEntry.info &&
                    <Box className={classes.tooltip}>
                      <TooltipComponent
                        title={dataEntry.info}
                        type='html'
                        child={<InfoIcon color='primary' />}
                        placement='right'
                        arrow
                      />
                    </Box>}
                </>}
              {dataEntry.inputType === 'checkbox' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      id={dataEntry.name}
                      {...register(dataEntry.name as any)}
                      className={`${
                        errors[dataEntry.name] ? classes.isInvalid : ''
                      }`}
                    />
                  }
                  label={
                    <Typography
                      variant='body2'
                      className={`${errors[dataEntry.name] ? classes.isInvalid : ''}`}
                      dangerouslySetInnerHTML={{ __html: dataEntry.info ? dataEntry.info : '' }}
                    />
                  }
                />)}
            </Grid>
          )
        })}
        {captchaPicture && (
          <Grid item xs={12}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' width='100%' spacing={2}>
              <ImageComponent width={290} height={80} src={'data:image/png;base64,' + captchaPicture} fallback='/src/shared/assets/images/icon-img-placeholder.svg' />
              <Box>
                <IconButton size='large' onClick={() => fetchCaptcha()}>
                  <RefreshIcon fontSize='large' />
                </IconButton>
              </Box>
            </Stack>
            <TextFieldComponent
              {...register('captcha')}
              id='captcha'
              type='text'
              size='small'
              inputProps={{ placeholder: t('login:enterCaptcha') }}
              required
              error={!!errors.captcha}
              sx={{ marginBottom: 0 }}
              InputProps={{ autoComplete: 'new-password' }}
              fullWidth
            />
          </Grid>
        )}
        <Stack direction='row' spacing={2} sx={{ pt: 5 }}>
          <Box>
            <ButtonComponent
              variant='text'
              startIcon={<KeyboardDoubleArrowLeftIcon />}
              onClick={onCancelEdit}
              color='secondary'
              text={t('login:cancel')}
            />
          </Box>
          <Box>
            <PreloaderButton
              position='left'
              loading={props.loading}
              disableButton={!isDirty || props.loading}
              color='primary'
              text={t('login:sendRegistration')}
              onClick={handleSubmit((data) => props.callbackOnSubmit(captcha, data as IRegistrationForm))}
            />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default RegisterForm
